import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DotsSixVertical, Pencil, Trash } from '@phosphor-icons/react';
import { COLORS } from 'assets/styled';
import Icon from 'components/Icon/Icon';
import Button from 'components/Button/Button';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import Draggable from 'components/DragAndDrop/Draggable/Draggable';
import { DangerModal } from 'components/Modals/DangerModal/DangerModal';
import { Level, upLevel } from 'components/Modals/Modal/type';
import { useFormatMoney } from 'utils/hooks/useFormatMoney';
import {
  IConfigurationQuestion,
  IConfigurationQuestionOption,
  ICreateOptionData,
  IUpdateOptionData,
} from 'types/ConfigurationQuestion.types';
import {
  useCreateQuestionOption,
  useDeleteQuestionOption,
  useReorderQuestionOptions,
  useUpdateConfigurationQuestion,
  useUpdateQuestionOption,
} from './hooks';
import { useReorderableList } from './reorder';
import { OptionFormModal } from './OptionFormModal/OptionFormModal';
import {
  DragHandle,
  EmptyState,
  IconButton,
  InlineButtonRow,
  List,
  PriceText,
  Row,
  RowActions,
  RowLabel,
  RowMain,
  RowSubInfo,
  SlugBadge,
  ToggleCell,
  ToggleLabel,
} from './OptionsManager.styled';

interface IOptionsManagerProps {
  question: IConfigurationQuestion;
  configurationId: number;
  currency: string;
  level: Level;
}

export const OptionsManager = ({
  question,
  configurationId,
  currency,
  level,
}: IOptionsManagerProps) => {
  const { t } = useTranslation();
  const formatMoney = useFormatMoney(currency);
  const options = question.options || [];

  const { mutate: createOption, isLoading: isCreating } =
    useCreateQuestionOption(question.id);
  const { mutate: updateOption } = useUpdateQuestionOption(question.id);
  const { mutate: deleteOption } = useDeleteQuestionOption(question.id);
  const { mutate: reorderOptions } = useReorderQuestionOptions(question.id);
  const { mutate: updateQuestion } =
    useUpdateConfigurationQuestion(configurationId);

  // A question can't stay active without options, and the active toggle is
  // disabled once there are none, so deleting the last option must deactivate it.
  const handleDeleteOption = (optionId: number) => {
    if (options.length === 1 && question.is_active) {
      updateQuestion(
        { questionId: question.id, data: { is_active: false } },
        { onSuccess: () => deleteOption(optionId) }
      );
    } else {
      deleteOption(optionId);
    }
  };

  const { ordered, setOrdered, draggedId, setDraggedId, handleDragHover } =
    useReorderableList<IConfigurationQuestionOption>(options, (ids) =>
      reorderOptions({ option_ids: ids })
    );

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [optionForEditing, setOptionForEditing] =
    useState<IConfigurationQuestionOption | null>(null);
  const [optionForDeletion, setOptionForDeletion] =
    useState<IConfigurationQuestionOption | null>(null);

  const closeForm = () => {
    setIsFormOpen(false);
    setOptionForEditing(null);
  };

  const handleAdd = (data: ICreateOptionData) => {
    createOption(data, { onSuccess: closeForm });
  };

  const handleEdit = (data: IUpdateOptionData) => {
    if (!optionForEditing) return;
    updateOption(
      { optionId: optionForEditing.id, data },
      { onSuccess: closeForm }
    );
  };

  const handleSetDefault = (option: IConfigurationQuestionOption) => {
    // Toggle: turning a default off is allowed (a question may have no default).
    const newIsDefault = !option.is_default;
    setOrdered((prev) =>
      prev.map((current) => {
        if (current.id === option.id) {
          return { ...current, is_default: newIsDefault };
        }
        // Setting a new default optimistically clears the others.
        return newIsDefault ? { ...current, is_default: false } : current;
      })
    );
    updateOption({ optionId: option.id, data: { is_default: newIsDefault } });
  };

  return (
    <>
      {ordered.length ? (
        <List>
          {ordered.map((option, index) => (
            <Draggable
              key={option.id}
              type="option"
              index={index}
              id={option.id}
              draggedId={draggedId}
              setDraggedId={setDraggedId}
              onDragHover={handleDragHover}
              isDragDisabled={ordered.length < 2}
            >
              <Row>
                <DragHandle>
                  <Icon
                    svg={DotsSixVertical}
                    color={COLORS.BORDER}
                    weight="bold"
                  />
                </DragHandle>
                <RowMain>
                  <RowLabel>{option.label}</RowLabel>
                  <RowSubInfo>
                    <SlugBadge>{option.slug}</SlugBadge>
                    <PriceText>{formatMoney(option.price)}</PriceText>
                  </RowSubInfo>
                </RowMain>
                <RowActions>
                  <ToggleCell>
                    <ToggleLabel>{t('Default')}</ToggleLabel>
                    <ToggleSwitch
                      isChecked={option.is_default}
                      onClick={() => handleSetDefault(option)}
                    />
                  </ToggleCell>
                  <IconButton
                    onClick={() => {
                      setOptionForEditing(option);
                      setIsFormOpen(true);
                    }}
                  >
                    <Icon svg={Pencil} size={20} color={COLORS.GREEN} />
                  </IconButton>
                  <IconButton onClick={() => setOptionForDeletion(option)}>
                    <Icon svg={Trash} size={20} color={COLORS.RED_100} />
                  </IconButton>
                </RowActions>
              </Row>
            </Draggable>
          ))}
        </List>
      ) : (
        <EmptyState>{t('No options yet')}</EmptyState>
      )}

      <InlineButtonRow>
        <Button
          secondary
          label={t('Add option')}
          height="36rem"
          onClick={() => {
            setOptionForEditing(null);
            setIsFormOpen(true);
          }}
        />
      </InlineButtonRow>

      <OptionFormModal
        isOpen={isFormOpen}
        level={upLevel(level)}
        currency={currency}
        optionForEditing={optionForEditing}
        onClose={closeForm}
        onAdd={handleAdd}
        onEdit={handleEdit}
        isLoading={isCreating}
      />

      <DangerModal
        level={upLevel(level)}
        isOpen={!!optionForDeletion}
        setIsOpen={(open) => !open && setOptionForDeletion(null)}
        title={t('Delete option')}
        description={t('Are you sure you want to delete this option?')}
        submit={{
          onClick: () => {
            if (optionForDeletion) handleDeleteOption(optionForDeletion.id);
            setOptionForDeletion(null);
          },
          text: t('Confirm'),
          disabled: false,
        }}
        cancel={{ onClick: () => setOptionForDeletion(null) }}
      />
    </>
  );
};
